import { useCallback, useEffect, useRef, useState } from "react";
import * as api from "../api";
import { Ic } from "./ui";
import { useT } from "../i18n";

/** Numele proiectului — în două locuri, cu o singură sursă.
 *
 *  În antet numele se CITEȘTE: „Optimus Build / Casa Iovu". În interiorul unui
 *  proiect toate ecranele arată cifre care nu spun al cui sunt, iar cu două
 *  file deschise pe două proiecte (cazul obișnuit când se compară două soluții)
 *  numele din antet e singurul lucru care le deosebește.
 *
 *  Redenumirea s-a mutat în „Dosarul proiectului": acolo stau actele din care
 *  se AFLĂ numele adevărat (proiectele se creează cu nume de lucru,
 *  „Proiectul 1"), deci acolo e și locul unde se corectează. Un câmp editabil
 *  în bara de sus e o capcană într-o bară care altfel se citește: e cel mai
 *  ușor lucru de atins din greșeală de pe fiecare ecran al aplicației.
 *
 *  Numele îl ține control-plane-ul (`core.project.name`), deci redenumirea e un
 *  PATCH pe proiect — nu o preferință locală a interfeței. */

/* Antetul și pagina stau în ramuri diferite ale arborelui, fără vreun store
   comun, iar fiecare își cere numele singur. Fără anunțul de pe `window`,
   antetul ar rămâne cu numele vechi până la o reîncărcare a filei — exact
   contrariul a ce vrea omul care tocmai a redenumit proiectul. */
const EVENIMENT = "proiect:nume";

export function useNumeProiect(id: string) {
  const [nume, setNume] = useState<string | null>(null);
  // Rolul vine din același răspuns ca numele, deci nu costă o cerere în plus —
  // iar redenumirea cere drept de SCRIERE, deci cine desenează câmpul de nume
  // trebuie să știe dacă are voie să-l deschidă.
  const [rol, setRol] = useState<api.ProjectRole | null>(null);

  useEffect(() => {
    let viu = true;
    setNume(null);
    setRol(null);
    api.getProject(id)
      .then((p) => {
        if (!viu) return;
        setNume(p.name);
        setRol(p.role);
      })
      .catch(() => undefined);
    return () => {
      viu = false;
    };
  }, [id]);

  useEffect(() => {
    const asculta = (e: Event) => {
      const d = (e as CustomEvent<{ id: string; nume: string }>).detail;
      if (d?.id === id) setNume(d.nume);
    };
    window.addEventListener(EVENIMENT, asculta);
    return () => window.removeEventListener(EVENIMENT, asculta);
  }, [id]);

  const redenumeste = useCallback(async (nou: string) => {
    const p = await api.renameProject(id, nou);
    window.dispatchEvent(
      new CustomEvent(EVENIMENT, { detail: { id, nume: p.name } }),
    );
    return p.name;
  }, [id]);

  return { nume, rol, redenumeste };
}

/** Numele din antet, după marcă. Doar de citit. */
export default function NumeProiect({ id }: { id: string }) {
  const { nume } = useNumeProiect(id);
  if (nume === null) return null;
  return (
    <span className="crumb crumb-nume">
      /&nbsp;&nbsp;<b>{nume}</b>
    </span>
  );
}

/** Antetul paginii de dosar: numele proiectului ca titlu — și titlul e locul în
 *  care se redenumește. Clicul pe el deschide un câmp cu ACEEAȘI tipografie, ca
 *  rândul să nu sară sub mână: Enter (sau „Salvează") scrie, Escape (sau
 *  „Renunță") lasă numele cum era.
 *
 *  Eticheta „DOSARUL PROIECTULUI" de deasupra a fost scoasă: bara de pași de
 *  chiar deasupra ei spune deja „Dosar", iar două titluri unul sub altul pentru
 *  același ecran împing numele — singurul lucru care se citește acolo — mai jos
 *  fără să adauge nimic. */
export function TitluProiect({ id }: { id: string }) {
  const t = useT();
  const { nume, rol, redenumeste } = useNumeProiect(id);
  const potScrie = rol === "editor" || rol === "admin";
  const [editare, setEditare] = useState(false);
  const [text, setText] = useState("");
  const [eroare, setEroare] = useState("");
  const [salvare, setSalvare] = useState(false);
  const [salvat, setSalvat] = useState(false);
  const input = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setEditare(false);
  }, [id]);

  useEffect(() => {
    if (editare) input.current?.select();
  }, [editare]);

  // Confirmarea nu rămâne pe ecran: e o notă despre ce s-a întâmplat acum o
  // clipă, nu o stare a paginii.
  useEffect(() => {
    if (!salvat) return;
    const t = setTimeout(() => setSalvat(false), 2400);
    return () => clearTimeout(t);
  }, [salvat]);

  function deschide() {
    setText(nume ?? "");
    setEroare("");
    setSalvat(false);
    setEditare(true);
  }

  async function salveaza() {
    const nou = text.trim();
    if (!nou || nou === nume) {
      setEditare(false);
      return;
    }
    setSalvare(true);
    setEroare("");
    try {
      await redenumeste(nou);
      setEditare(false);
      setSalvat(true);
    } catch (e) {
      // Numele rămâne în câmp: o eroare de rețea n-are voie să arunce ce a
      // scris omul.
      setEroare(e instanceof api.ApiError && e.message
        ? e.message : t("nume_proiect.esec"));
    } finally {
      setSalvare(false);
    }
  }

  return (
    <div className="titlu-proiect">
      {editare ? (
        <div className="titlu-rand">
          <input ref={input} className="titlu-input" value={text} autoFocus
                 disabled={salvare} aria-label={t("nume_proiect.camp")}
                 onChange={(e) => setText(e.target.value)}
                 onKeyDown={(e) => {
                   if (e.key === "Enter") salveaza();
                   if (e.key === "Escape") setEditare(false);
                 }} />
          <button className="btn btnp" onClick={salveaza} disabled={salvare}>
            <Ic n="check" s={14} />{t(salvare ? "comun.se_salveaza" : "comun.salveaza")}
          </button>
          <button className="btn" onClick={() => setEditare(false)}
                  disabled={salvare}>{t("comun.renunta")}</button>
          {eroare && <span className="titlu-eroare">{eroare}</span>}
        </div>
      ) : (
        <div className="titlu-rand">
          {/* Titlul rămâne titlu pentru toată lumea; creionul și clicul apar
              doar cui i-ar merge salvarea (v. api/projects.py). */}
          <button className="titlu-nume" onClick={deschide}
                  disabled={nume === null || !potScrie}
                  title={t(potScrie ? "nume_proiect.redenumeste"
                                    : "comun.doar_citire")}>
            <span className="disp">{nume ?? "…"}</span>
            {potScrie && <Ic n="pen" s={14} />}
          </button>
          {salvat && (
            <span className="titlu-salvat"><Ic n="check" s={12} />{t("nume_proiect.salvat")}</span>
          )}
        </div>
      )}
    </div>
  );
}
